import React, { useEffect, useRef, useState } from "react";
import { Palette, Check, ChevronsUpDown } from "lucide-react";
import { THEMES, useTheme } from "@/lib/theme";
import { TID } from "@/lib/testIds";

const Swatch = ({ t, size = 14 }) => (
    <span
        className="rounded-full shrink-0 ring-1 ring-white/25"
        style={{ width: size, height: size, background: `linear-gradient(135deg, ${t.swatch} 0%, ${t.swatch} 48%, ${t.swatch2} 52%, ${t.swatch2} 100%)` }}
    />
);

/** Theme gallery picker — six visual identities, opened from the sidebar footer.
 * The popover is fixed-positioned off the trigger rect so it can never be
 * clipped by the sidebar's scroll container. */
export function ThemeSwitcher({ collapsed = false }) {
    const { theme, setTheme } = useTheme();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const active = THEMES.find((t) => t.id === theme) || THEMES[0];

    const toggleOpen = () => {
        const r = triggerRef.current?.getBoundingClientRect();
        if (r) setPos({ left: collapsed ? r.right + 8 : r.left, bottom: window.innerHeight - r.top + 8, width: collapsed ? 248 : Math.max(r.width, 236) });
        setOpen((o) => !o);
    };

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        const onDown = (e) => {
            if (panelRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown, true);
        return () => {
            window.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown, true);
        };
    }, [open]);

    return (
        <div data-testid={TID.themeSwitcher} className={collapsed ? "px-1 py-2" : "px-2 py-2"}>
            {!collapsed && (
                <div className="label-eyebrow mb-1.5 flex items-center gap-1.5">
                    <Palette size={11} strokeWidth={1.5} /> Theme
                </div>
            )}

            <button
                ref={triggerRef}
                data-testid={TID.themeSwitcherBtn}
                onClick={toggleOpen}
                aria-haspopup="listbox"
                aria-expanded={open}
                title={`Theme — ${active.label}`}
                className={
                    collapsed
                        ? "w-full flex items-center justify-center py-2 text-slate-500 hover:text-cyan-300 transition-colors"
                        : "w-full flex items-center gap-2 px-2.5 py-2 rounded-full border border-white/10 bg-black/40 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-200 transition-colors"
                }
            >
                {collapsed ? (
                    <Palette size={16} strokeWidth={1.5} />
                ) : (
                    <>
                        <Swatch t={active} />
                        <span className="flex-1 text-left text-[10px] font-mono uppercase tracking-wider truncate">{active.short}</span>
                        <ChevronsUpDown size={12} strokeWidth={1.5} className="opacity-60" />
                    </>
                )}
            </button>

            {open && pos && (
                <div
                    ref={panelRef}
                    data-testid={TID.themeSwitcherPanel}
                    role="listbox"
                    className="fixed z-[61] p-1.5 rounded-xl hud-glass"
                    style={{ left: pos.left, bottom: pos.bottom, width: pos.width }}
                >
                    <div className="label-eyebrow px-2 py-1.5">Visual Identity</div>
                    {THEMES.map((t) => (
                        <button
                            key={t.id}
                            role="option"
                            aria-selected={theme === t.id}
                            data-testid={TID.themeOption(t.id)}
                            onClick={() => { setTheme(t.id); setOpen(false); }}
                            className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left transition-colors ${
                                theme === t.id ? "bg-cyan-500/15 text-cyan-200" : "text-slate-400 hover:bg-white/[0.06] hover:text-slate-100"
                            }`}
                        >
                            <Swatch t={t} size={16} />
                            <span className="min-w-0 flex-1">
                                <span className="block text-[11px] font-mono uppercase tracking-wider truncate">{t.label}</span>
                                <span className="block text-[10px] text-slate-500 truncate">{t.blurb}</span>
                            </span>
                            {theme === t.id && <Check size={13} strokeWidth={2} className="shrink-0" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
